import isNil from 'lodash/isNil';
import { push } from 'connected-react-router';

import { libraryManagedConfig, selectionlistConfig } from '../../config';
import {
    globalErrorResponse,
    globalWarning,
    showNotification,
    showNotificationWithInfo,
    TYPE_WARNING
} from '../notifications/actions';
import {
    buildBulkRequestPathParamsAndData,
    deselectAll,
    deselectMultiple,
    determineCurrentPage,
    determineGrouping,
    determineSourceParams,
    selectAll,
    selectMultiple,
    setSelectionMode
} from '../productSelection/actions';
import { getAgeCategoryParam } from '../ageCategory/actions';
import * as consts from '../constants';
import {
    ACTION_CHANGE_PRICE,
    ACTION_DECREASE,
    ACTION_INCREASE,
    ACTION_MOVE,
    ACTION_REMOVE,
    ACTION_SET,
    ListContentSource
} from '../constants';
import Utils from '../../shared/utils';

import * as types from './types';
import {
    CHANGE_ACTIVE_SELECTIONLIST,
    ChangeActiveSelectionlistAction,
    SELECTIONLIST_SEND_FOR_APPROVAL_ERROR,
    SELECTIONLIST_SEND_FOR_APPROVAL_REQUEST,
    SELECTIONLIST_SEND_FOR_APPROVAL_SUCCESS,
    SELECTIONLIST_SUMMARY_SUCCESS,
    SELECTIONLIST_TOGGLE_SELECTIONBAR,
    SELECTIONLISTS_FAILURE,
    SELECTIONLISTS_REQUEST,
    SELECTIONLISTS_SUCCESS,
    SelectionlistSendForApprovalErrorAction,
    SelectionlistSendForApprovalRequestAction,
    SelectionlistSendForApprovalSuccessAction,
    SelectionlistsRequestAction,
    SelectionlistsSuccessAction,
    SelectionlistSummarySuccessAction,
    SelectionlistToggleSelectionbarAction,
    SET_ACTIVE_LIST,
    SET_LIST_SHARED_STATE,
    SetActiveListAction
} from './types';
import { AppDispatch, AppThunkAction, GetAppState } from '..';
import { copiesListUsageConditionUpdate } from '../copiesList/actions';
import { EditListResponse, ListContentApiResponse, SelectionListOut, UnavailableProductOut } from '../api';
import { removeFromCart } from '../products/actionsCart';
import { Price, SubscriptionPeriodType } from '../../../models/Article';
import { ListSourceType } from '../../../models/ListSourceType';
import { log } from '../../shared/utils/log';
import { invalidateSelectionListProducts } from '../../shop/selectionlists/api/useSelectionListProducts';
import { invalidateSelectionListSummary } from '../../api/selection-list/useSelectionListSummary';
import { invalidateShoppingCartProducts } from '../../api/shopping-cart/useShoppingCartProducts';
import { invalidateCartGroupedItems } from '../../api/shopping-cart/useCartGroupedItems';
import { invalidateSelectionListGroupedItems } from '../../api/selection-list/useSelectionListGroupedItems';
import { invalidateSelectionListAggregations } from '../../shop/selectionlists/useSelectionListAggregations';
import { disableListLoading, enableListLoading } from '../listLoading/actions';
import i18n from '../../../i18n';
import { http } from '../../api/axios';

const config = selectionlistConfig();

const requestSelectionlists = (): SelectionlistsRequestAction => {
    return { type: SELECTIONLISTS_REQUEST };
};
const receiveSelectionlists = (resp: Array<SelectionListOut>): SelectionlistsSuccessAction => {
    return { type: SELECTIONLISTS_SUCCESS, payload: resp };
};
export const receiveSelectionlistSummary = (resp: SelectionListOut): SelectionlistSummarySuccessAction => {
    return { type: SELECTIONLIST_SUMMARY_SUCCESS, payload: resp };
};

export type OrderListActionUpdateName = { id: string; name: string };
export type OrderListActionVisibility = { listId: string; shared: boolean };

export function failSelectionlistSummary(error: any) {
    return (dispatch: AppDispatch) => {
        dispatch(globalErrorResponse(error));
        if (error.response && error.response.status === 404) {
            dispatch(push(consts.PATH_CATALOGUE));
            dispatch(fetchSelectionlists());
        } else {
            log.error(i18n.t('notifications.failSelectionlistSummary.error'), error);
        }
        dispatch({
            type: types.SELECTIONLIST_SUMMARY_FAILURE,
            error: true
        });
    };
}

export const approvalStatusError = (error: string): SelectionlistSendForApprovalErrorAction => {
    return { type: SELECTIONLIST_SEND_FOR_APPROVAL_ERROR, payload: error };
};

export function failSelectionlists(error, source?, callback?) {
    return (dispatch: AppDispatch, getState: GetAppState) => {
        let existingList = getState().orderlists.list;
        if (!error.runtimeDuplication) {
            switch (error.response.status) {
                case 404:
                    dispatch(globalWarning(error));
                    existingList = [];
                    break;

                case 409: {
                    const data = error.response.data;
                    if (data.additionalInfo) {
                        const infoData = data.additionalInfo.map(p => Utils.withdrawnProductLine(p)).join('<br/>');
                        const isSelectedAllRequest = getState().productSelection.all;
                        const failMessage = isSelectedAllRequest
                            ? i18n.t('notifications.failSelectionlists.fail')
                            : i18n.t('notifications.failSelectionlists.proceed');
                        const showCancelButton = !isSelectedAllRequest;
                        dispatch(
                            showNotificationWithInfo(
                                data.message,
                                failMessage,
                                infoData,
                                data.additionalInfo,
                                products => {
                                    withdrawnCallback(dispatch, source, products);
                                    if (callback) {
                                        setTimeout(callback(), 1000);
                                    } else if (!isSelectedAllRequest) {
                                        const productIds = data.additionalInfo.map(product => product.id);
                                        dispatch(deselectMultiple(productIds));
                                    } else {
                                        dispatch(setSelectionMode(false));
                                    }
                                },
                                showCancelButton
                            )
                        );
                        break;
                    }
                    dispatch(globalErrorResponse(error));
                    break;
                }
                default:
                    dispatch(globalErrorResponse(error));
            }
        }

        dispatch({
            type: SELECTIONLISTS_FAILURE,
            payload: existingList,
            error: true
        });
    };
}

export const toggleSelectionBar = (): SelectionlistToggleSelectionbarAction => {
    return { type: SELECTIONLIST_TOGGLE_SELECTIONBAR };
};

//TODO: consider renaming since it now also removes comingSoonProducts
export async function withdrawnCallback(
    dispatch: AppDispatch,
    source: ListContentSource,
    unavailableProducts: UnavailableProductOut[]
) {
    if ([consts.SOURCE_CART, consts.SOURCE_SELECTIONLIST].includes(source)) {
        dispatch(selectMultiple(unavailableProducts.map(p => p.lineItemKey)));
        let promise;
        if (source === consts.SOURCE_CART) {
            promise = dispatch(removeFromCart());
        } else {
            promise = dispatch(removeProductsFromList());
        }
        await promise.then(() => dispatch(deselectAll()));
    }
}

export function fetchUnavailableProducts(listId: string) {
    return (dispatch: AppDispatch) => {
        http.get(config.endpointUnvailableProducts, { pathParams: { listId } }).catch(error =>
            dispatch(globalErrorResponse(error))
        );
    };
}

// TODO implement v2 endpoint and remove updateProductInList
export const removeProductFromList = (productIdOrOptions, listId) => {
    const {
        id,
        price = undefined,
        subscriptionPeriodType = undefined
    } = typeof productIdOrOptions === 'string' ? { id: productIdOrOptions } : productIdOrOptions;
    const usageCondition = price
        ? { lendingModel: price.lendingModel, usageLimit: price.usageLimit, termLimit: price.termLimit }
        : undefined;
    return updateProductInList({
        ...productIdOrOptions,
        productId: id,
        action: consts.ACTION_REMOVE,
        actionType: types.REMOVE_FROM_LIST,
        targetListId: listId,
        numCopies: null,
        callback: null,
        usageCondition,
        targetSubscriptionPeriodType: subscriptionPeriodType
    });
};

function getMessageFromAction(action, productId) {
    switch (action) {
        case consts.ACTION_MOVE:
            return i18n.t('notifications.getMessageFromActionList.move', { productTitle: productId });
        case consts.ACTION_COPY:
            return i18n.t('notifications.getMessageFromActionList.copy', { productTitle: productId });
        case consts.ACTION_REMOVE:
            return i18n.t('notifications.getMessageFromActionList.remove', { productTitle: productId });
        case consts.ACTION_ADD:
            return i18n.t('notifications.getMessageFromActionList.add', { productTitle: productId });
        default:
            return null;
    }
}

function updateProductInList({
    productId,
    action,
    actionType,
    targetListId,
    numCopies,
    callback,
    usageCondition,
    sourceOverwrite,
    alreadyInListCheck = true,
    targetSubscriptionPeriodType,
    sourceSubscriptionPeriodType
}: {
    productId: string;
    action: string;
    actionType: any;
    targetListId: string;
    numCopies?: number | null;
    callback?: (() => Promise<void> | void) | null;
    usageCondition?: Price;
    sourceOverwrite?: ListSourceType;
    alreadyInListCheck?: boolean;
    targetSubscriptionPeriodType?: SubscriptionPeriodType;
    sourceSubscriptionPeriodType?: SubscriptionPeriodType;
}): AppThunkAction<Promise<unknown>> {
    return (dispatch: AppDispatch, getState: GetAppState) => {
        const source = determineCurrentPage(getState);

        const sourceListId = getState().orderlist.id ?? '';

        const data = {
            action,
            amount: numCopies,
            ...determineSourceParams(getState),
            ...determineGrouping(getState),
            targetUsageCondition: usageCondition,
            targetSubscriptionPeriodType,
            sourceSubscriptionPeriodType
        };

        if (sourceOverwrite) {
            data.source = sourceOverwrite;
        }

        dispatch({
            type: actionType,
            payload: data
        });
        if (
            action === ACTION_MOVE ||
            action === ACTION_REMOVE ||
            action === ACTION_DECREASE ||
            action === ACTION_INCREASE ||
            action === ACTION_SET ||
            action === ACTION_CHANGE_PRICE
        ) {
            dispatch(enableListLoading());
        }

        const response = http.put<EditListResponse>(config.endpointProducts, data, {
            pathParams: {
                listId: targetListId,
                productId
            },
            params: { alreadyInListCheck }
        });
        response
            .then(() => {
                dispatch(showNotification(getMessageFromAction(action, productId)));
                dispatch(fetchSelectionlists());
                dispatch(copiesListUsageConditionUpdate([productId]));
                if (sourceListId) {
                    invalidateShoppingCartProducts().then();
                    invalidateCartGroupedItems().then();
                    invalidateSelectionListSummary(sourceListId).then();
                    invalidateSelectionListGroupedItems(sourceListId).then();
                }

                if (actionType === types.MOVE_TO_LIST) {
                    invalidateSelectionListAggregations(sourceListId).then();
                    invalidateSelectionListProducts(sourceListId).then();
                    invalidateShoppingCartProducts().then();
                    invalidateCartGroupedItems().then();
                    invalidateSelectionListGroupedItems(sourceListId).then();
                } else if (actionType === types.REMOVE_FROM_LIST) {
                    invalidateSelectionListAggregations(sourceListId).then();
                    invalidateSelectionListProducts(sourceListId).then();
                    return;
                } else if (actionType === types.COPY_TO_LIST) {
                    invalidateSelectionListProducts(sourceListId).then();
                }
                if (source === consts.SOURCE_SELECTIONLIST) {
                    invalidateSelectionListGroupedItems(sourceListId).then();
                    invalidateSelectionListProducts(sourceListId).then();
                } else if (source === consts.SOURCE_CART) {
                    invalidateCartGroupedItems().then();
                }

                if (callback) {
                    callback();
                }
            })
            .catch(error => dispatch(failSelectionlists(error, data.source)))
            .finally(() => {
                dispatch(disableListLoading());
            });

        // Return the first promise so the consumers of this action can react depending on the success
        // of the action. If we return the promise returned from the `catch`, information about failure
        // is lost.
        return response;
    };
}

export function copyProductsToList(targetListId: string, redirectToTargetList?: boolean, isLibraryManaged?: boolean) {
    return (dispatch: AppDispatch, state: GetAppState) => {
        let callback: (() => void) | null = null;
        if (redirectToTargetList) {
            callback = function () {
                dispatch(push(`${consts.PATH_SELECTIONLISTS}/${targetListId}`));
            };
        }
        updateProductsInList(
            targetListId,
            isLibraryManaged ? consts.ACTION_ADD : consts.ACTION_COPY,
            null,
            types.COPY_TO_LIST,
            i18n.t('notifications.copyProductsToList.message'),
            dispatch,
            state,
            callback
        );
    };
}

export function addProductsToGroup(sourceListId, productGroupId) {
    return (dispatch: AppDispatch, state: GetAppState) => {
        const endpoint = libraryManagedConfig().endpointProductGroupBulkSource;
        let requestdata;
        try {
            requestdata = buildBulkRequestPathParamsAndData(consts.ACTION_COPY, 1, state, false);
        } catch (error) {
            dispatch(showNotification(i18n.t('notifications.addProductsToGroup.warning'), TYPE_WARNING));
            return;
        }
        const pathParams = requestdata.pathParams;
        pathParams.productGroupId = productGroupId;
        const payload = requestdata.data;

        http.put(endpoint, payload, { pathParams })
            .then(() => {
                dispatch(deselectAll());
                dispatch(showNotification(i18n.t('notifications.addProductsToGroup.success')));
                dispatch(fetchSelectionlists());
                // TODO [PORTAL-3451] this seems to cause a broken update to the currently visible selection list.
                //  It looks like it is a quick'n'dirty hack to enforce an active selection list, but it causes the
                //  currently visible selection list meta data to get lost.
                //  If no unwanted side effect appear, the following line can be removed.
                // dispatch(changeActiveSelectionlist(sourceListId));
            })
            .catch(error => dispatch(failSelectionlists(error, payload.source)));
    };
}

export function removeProductsFromList() {
    return (dispatch: AppDispatch, state: GetAppState) => {
        const currentListId = state().orderlist?.id;
        if (!isNil(currentListId)) {
            return updateProductsInList(
                currentListId,
                consts.ACTION_REMOVE,
                0,
                types.REMOVE_FROM_LIST,
                i18n.t('notifications.removeProductsFromList.message'),
                dispatch,
                state
            );
        }
    };
}

// TODO replace this method with v2 endpoints of remove products / duplicate list
function updateProductsInList(
    targetListId,
    action,
    numCopies,
    actionType,
    message,
    dispatch: AppDispatch,
    state: GetAppState,
    callback?,
    isFilteredFromBundle = false,
    refine: string | null = null,
    priceTier?: string,
    availabilityCheck: boolean = false,
    alreadyInListCheck: boolean = true
): Promise<ListContentApiResponse> {
    const endpoint = config.endpointBulk;
    let requestdata;
    try {
        requestdata = buildBulkRequestPathParamsAndData(action, numCopies, state, isFilteredFromBundle);
    } catch (error) {
        dispatch(showNotification(i18n.t('notifications.updateProductInList.warning'), TYPE_WARNING));
        return Promise.reject(error);
    }
    const pathParams = requestdata.pathParams;
    const payload = requestdata.data;

    pathParams.listId = targetListId;

    const params = { availabilityCheck, alreadyInListCheck };
    if (action === consts.ACTION_ADD) {
        params['ageCategory'] = getAgeCategoryParam(state);
    } else if (action === consts.ACTION_SET) {
        pathParams.source = consts.SOURCE_QUANTITY;
    }

    if (refine !== null) {
        payload.refine = refine;
    }

    if (priceTier) {
        payload.targetPrice = priceTier;
    }

    if (!availabilityCheck) {
        dispatch({
            type: actionType,
            payload
        });
    }
    if (
        action === ACTION_MOVE ||
        action === ACTION_REMOVE ||
        action === ACTION_DECREASE ||
        action === ACTION_INCREASE ||
        action === ACTION_SET ||
        action === ACTION_CHANGE_PRICE
    ) {
        dispatch(enableListLoading());
    }

    return http
        .put<ListContentApiResponse>(endpoint, payload, { params, pathParams })
        .then(response => response.data)
        .then(data => {
            if (!availabilityCheck) {
                dispatch(deselectAll());
                dispatch(showNotification(message));
                dispatch(fetchSelectionlists());

                if (callback) {
                    callback();
                } else {
                    if (payload.sourceId === targetListId || payload.sourceId === state().orderlist.id) {
                        invalidateSelectionListSummary(payload.sourceId).then();
                        invalidateSelectionListAggregations(payload.sourceId).then();
                        invalidateSelectionListGroupedItems(payload.sourceId).then();
                        invalidateSelectionListProducts(payload.sourceId).then();
                    }
                    // TODO [PORTAL-3451] this seems to cause a broken update to the currently visible selection list.
                    //  It looks like it is a quick'n'dirty hack to enforce an active selection list, but it causes the
                    //  currently visible selection list meta data to get lost.
                    //  If no unwanted side effect appear, the following line can be removed.
                    // dispatch(changeActiveSelectionlist(targetListId));
                }
            }
            return data;
        })
        .catch(error => {
            if (!availabilityCheck) {
                dispatch(failSelectionlists(error, payload.source));
            } else {
                dispatch(globalErrorResponse(error));
            }
            return Promise.reject(error);
        })
        .finally(() => {
            dispatch(disableListLoading());
        });
}

type FetchSelectionlistsOptions = {
    /**
     * Force the selection lists to be reloaded, even if a request for refreshing them seems to be in progress.
     * This was added in order to fix PORTAL-3560
     */
    readonly force?: boolean;
};

export function fetchSelectionlists({ force = false }: FetchSelectionlistsOptions = {}) {
    return (dispatch: AppDispatch, getState: GetAppState) => {
        if (getState().orderlists.loading && !force) {
            return;
        }

        dispatch(requestSelectionlists());
        http.get(config.endpoint)
            .then(response => {
                dispatch(receiveSelectionlists(response.data));
            })
            .catch(error => dispatch(failSelectionlists(error)));
    };
}

export const changeActiveSelectionlist = (listId?: string): ChangeActiveSelectionlistAction => {
    return { type: CHANGE_ACTIVE_SELECTIONLIST, payload: listId };
};
export const setActiveList = (resp: SelectionListOut): SetActiveListAction => {
    return { type: SET_ACTIVE_LIST, payload: resp };
};

export const setListSharedState = (shared: boolean) => {
    return { type: SET_LIST_SHARED_STATE, payload: shared };
};

export function createAndGoToNewSelectionlist() {
    const name = i18n.t('notifications.createAndGoToNewSelectionlist.name');
    return (dispatch: AppDispatch) => {
        http.post(config.endpoint, { listName: name })
            .then(response => {
                const newListId = response.data;
                const list = {
                    id: newListId,
                    listName: name,
                    eaudiobooksAmount: 0,
                    eaudiobooksValue: 0,
                    ebooksAmount: 0,
                    ebooksValue: 0,
                    totalNetValue: 0,
                    totalTaxValue: 0,
                    totalValue: 0
                } as SelectionListOut;
                dispatch({
                    type: types.CREATE_SELECTIONLIST,
                    payload: list
                });
                dispatch(push(`${consts.PATH_SELECTIONLISTS}/${newListId}`));
            })
            .catch(error => dispatch(failSelectionlists(error)));
    };
}

export function createSelectionlist(name) {
    return (dispatch: AppDispatch) => {
        const body = JSON.stringify({ listName: name });
        http.post(config.endpoint, body)
            .then(response => {
                const list = {
                    id: response.data,
                    listName: name,
                    eaudiobooksAmount: 0,
                    eaudiobooksValue: 0,
                    ebooksAmount: 0,
                    ebooksValue: 0,
                    totalNetValue: 0,
                    totalTaxValue: 0,
                    totalValue: 0
                } as SelectionListOut;
                dispatch({
                    type: types.CREATE_SELECTIONLIST,
                    payload: list
                });
                dispatch(setActiveList(list));
                dispatch(fetchSelectionlists());
            })
            .catch(error => dispatch(failSelectionlists(error)));
    };
}

export function duplicateList() {
    return (dispatch: AppDispatch, state: GetAppState) => {
        const currentList = state().orderlist;
        dispatch(selectAll());

        const newName = i18n.t('notifications.duplicateList.newName', {
            listName: currentList.listName,
            defaultValue: ''
        });

        http.post(config.endpoint, { listName: newName })
            .then(response => {
                const newListId = response.data;
                dispatch(setActiveList({ id: newListId, listName: newName }));
                dispatch(copyProductsToList(newListId, true));
            })
            .catch(error => dispatch(failSelectionlists(error)));
    };
}

export function importBulkISBN(isbns: string): AppThunkAction<Promise<unknown>> {
    return (dispatch: AppDispatch, getState: GetAppState) => {
        if (!isbns || isbns === '') {
            dispatch(globalWarning(i18n.t('notifications.importBulkISBN.warning')));
            return Promise.reject(i18n.t('notifications.importBulkISBN.warning'));
        }
        dispatch(enableListLoading());
        const currentList = getState().orderlist;
        return http
            .post(
                config.endpointISBNBulkImport,
                { isbns },
                {
                    pathParams: { listId: currentList.id }
                }
            )
            .then(response => {
                const result = response.data;
                if (result && result.successful) {
                    dispatch(showNotification(response.data.response));

                    dispatch(setActiveList(currentList));
                    dispatch(fetchSelectionlists());
                    invalidateSelectionListAggregations(currentList.id!).then();
                    invalidateSelectionListSummary(currentList.id!).then();
                    invalidateSelectionListProducts(currentList.id!).then();
                    invalidateSelectionListGroupedItems(currentList.id!).then();
                } else {
                    const errorMessage = result.response || '';
                    dispatch(globalWarning(errorMessage));
                }
            })
            .catch(error => {
                dispatch(failSelectionlists(error));
                return Promise.reject(error);
            })
            .finally(() => {
                dispatch(disableListLoading());
            });
    };
}

export function deleteSelectionlist(listId: string) {
    return (dispatch: AppDispatch) => {
        dispatch({
            type: types.DELETE_SELECTIONLIST,
            payload: listId
        });

        dispatch(push(consts.PATH_CATALOGUE));
        http.delete(config.endpoint, {
            pathParams: {
                listId
            }
        })
            .then(() => dispatch(fetchSelectionlists({ force: true })))
            .catch(error => dispatch(failSelectionlists(error)));
    };
}

export function changeListVisibility(listId: string, shared: boolean) {
    return (dispatch: AppDispatch) => {
        const pathParams = { listId };
        let result;
        if (shared) {
            result = http.put(config.endpointVisibility, {}, { pathParams });
        } else {
            result = http.delete(config.endpointVisibility, { pathParams });
        }
        result
            .then(() => {
                dispatch({
                    type: types.SELECTIONLIST_VISIBILITY,
                    payload: { listId, shared } as OrderListActionVisibility
                });
            })
            .catch(error => dispatch(globalErrorResponse(error)));
    };
}

const sendForApprovalRequest = (): SelectionlistSendForApprovalRequestAction => {
    return { type: SELECTIONLIST_SEND_FOR_APPROVAL_REQUEST };
};
const sendForApprovalSuccess = (): SelectionlistSendForApprovalSuccessAction => {
    return { type: SELECTIONLIST_SEND_FOR_APPROVAL_SUCCESS };
};

export function sendForApproval(id, email) {
    return (dispatch: AppDispatch) => {
        const pathParams = { listId: id };
        const params = { userEmail: email };

        dispatch(sendForApprovalRequest());

        return http
            .get(config.endpointApproval, { params, pathParams })
            .then(() => {
                dispatch(showNotification(i18n.t('notifications.sendForApproval.success')));
                dispatch(sendForApprovalSuccess());
            })
            .catch(error => {
                dispatch(approvalStatusError(error.response.data.message));
                return Promise.reject();
            });
    };
}
